import { Notification } from "../../types/notification";
import { BackendHttpService } from "../backendHttpService";
import { RequestOptions } from "../baseService";

export interface NotificationConfiguration {
  id: number;
  configurationKey: string;
  configurationValue: string;
  description: string;
  configurationType: string;
}

export interface UpdateNotificationConfigurationRequest {
  configurationValue: string;
}

/**
 * Notification Admin Service
 *
 * Administrative endpoints for notification management including:
 * - Notification generation (testing purposes)
 * - Notification configuration management
 *
 * These endpoints are typically restricted to administrators.
 */
export class NotificationAdminService extends BackendHttpService {
  constructor() {
    super("notifications");
  }

  // ===== NOTIFICATION TESTING METHODS (ADMIN ONLY) =====

  /**
   * Generate all notifications for all users (admin testing)
   * POST /notifications/generate
   */
  async generateAllNotifications(
    options: RequestOptions = {}
  ): Promise<Notification[]> {
    try {
      return await this.post<{}, Notification[]>({}, "/generate", options);
    } catch (error) {
      console.error("Error generating all notifications:", error);
      throw error;
    }
  }

  /**
   * Generate all notifications for a specific person (admin testing)
   * POST /notifications/generate/person/{personId}
   */
  async generateNotificationsForPerson(
    personId: number,
    options: RequestOptions = {}
  ): Promise<Notification[]> {
    try {
      if (!personId || isNaN(personId)) {
        throw new Error("Invalid person ID for notification generation");
      }

      return await this.post<{}, Notification[]>(
        {},
        `/generate/person/${personId}`,
        options
      );
    } catch (error) {
      console.error(
        `Error generating notifications for person ${personId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Generate system notifications for a specific person (admin testing)
   * POST /notifications/generate/system-notifications/person/{personId}
   * Note: This generates notifications that managers typically receive
   */
  async generateSystemNotificationsForPerson(
    personId: number,
    options: RequestOptions = {}
  ): Promise<Notification[]> {
    try {
      if (!personId || isNaN(personId)) {
        throw new Error("Invalid person ID for system notification generation");
      }

      return await this.post<{}, Notification[]>(
        {},
        `/generate/system-notifications/person/${personId}`,
        options
      );
    } catch (error) {
      console.error(
        `Error generating system notifications for person ${personId}:`,
        error
      );
      throw error;
    }
  }

  // ===== NOTIFICATION CONFIGURATION METHODS (ADMIN ONLY) =====

  /**
   * Get notification configurations
   * GET /notifications/configurations
   */
  async getConfigurations(
    configurationType?: string,
    options: RequestOptions = {}
  ): Promise<NotificationConfiguration[]> {
    try {
      const params = new URLSearchParams();

      if (configurationType) {
        params.append("configurationType", configurationType);
      }

      const queryString = params.toString();
      const path = `/configurations${queryString ? `?${queryString}` : ""}`;

      return await this.get<NotificationConfiguration[]>(path, options);
    } catch (error) {
      console.error("Error fetching notification configurations:", error);
      throw error;
    }
  }

  /**
   * Update notification configuration
   * PUT /notifications/configurations/{id}
   */
  async updateConfiguration(
    id: number,
    configurationValue: string,
    options: RequestOptions = {}
  ): Promise<NotificationConfiguration> {
    try {
      if (!id || isNaN(id)) {
        throw new Error("Invalid configuration ID for update");
      }

      const request: UpdateNotificationConfigurationRequest = {
        configurationValue,
      };

      return await this.put<
        UpdateNotificationConfigurationRequest,
        NotificationConfiguration
      >(request, `/configurations/${id}`, options);
    } catch (error) {
      console.error(`Error updating notification configuration ${id}:`, error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const notificationAdminService = new NotificationAdminService();
